// src/api/centralFeaturesApi.ts
// Central Foundation feature flag batch evaluation API.
//
// ARCHITECTURE: Uses Authorization: Bearer <token> for auth.
// This is a Central bearer API endpoint, NOT cookie-based.
//
// INVARIANT: Feature flag evaluation never blocks boot.
// Failures result in all flags defaulting to false.
// Boot completion depends only on switch-context.

import { CENTRAL_URL } from '../config/centralAuth';
import type { AppDispatch } from '../store/store';
import {
  setFeatureFlags,
  setFeatureFlagsLoading,
  setFeatureFlagsError,
} from '../store/slices/featureFlagsSlice';

// ============================================================
// CRM Feature Flags
// ============================================================

/**
 * All CRM feature flag keys to evaluate.
 * Add new flags here as they are defined in Central.
 */
export const CRM_FEATURE_FLAGS = [
  'crm.ai.assistant',
  // Gates the additive, read-only "Workspace Priorities" section (server Execution
  // Engine projection). Fail-closed: off/unknown ⇒ section hidden. Backend also
  // gated by EXECUTION_ENGINE_ENABLED; either off ⇒ feature invisible.
  'crm.execution_engine.workspace_priorities',
  'crm.ai.email.compose',
  'crm.ai.email.summarize',
  'crm.bulk.operations',
  'crm.custom.fields',
  'crm.email.templates',
  'crm.export.advanced',
  'crm.import.advanced',
  'crm.integrations.calendar',
  'crm.integrations.email',
  'crm.reports.advanced',
  'crm.workflows.automation',
  // Gates the entire Work Accountability UX (PR-E). Fail-closed: off/unknown ⇒
  // everything hidden and the app behaves exactly as before. Mirrors the backend
  // per-workspace `workAccountabilityEnabled` launch flag (kept in sync by ops);
  // the backend independently fails closed (503) if its flag is off.
  'crm.work_accountability',
] as const;

export type CrmFeatureFlag = (typeof CRM_FEATURE_FLAGS)[number];

// ============================================================
// Central API Types
// ============================================================

/**
 * Central's feature evaluation request contract.
 */
interface FeatureEvaluationRequest {
  keys: readonly string[];
  context: {
    workspaceId: string;
  };
}

/**
 * Central's feature evaluation response contract.
 */
interface FeatureEvaluationResponse {
  ok: boolean;
  flags: Record<
    string,
    {
      enabled: boolean;
      reason: string;
      ruleId?: string;
    }
  >;
}

// ============================================================
// Feature Evaluation
// ============================================================

const FEATURE_EVALUATION_TIMEOUT_MS = 10_000;

/**
 * Evaluate feature flags via Central bearer API.
 *
 * POST {CENTRAL_URL}/api/features/evaluate
 * Authorization: Bearer <accessToken>
 *
 * Request body:
 * {
 *   "keys": [...],
 *   "context": { "workspaceId": "..." }
 * }
 *
 * Response:
 * {
 *   "ok": true,
 *   "flags": {
 *     "flag.key": { "enabled": true, "reason": "...", "ruleId": "..." }
 *   }
 * }
 *
 * INVARIANT: This function never blocks boot.
 * Failures result in all flags defaulting to false.
 *
 * @param accessToken - Central access token (bearer)
 * @param workspaceId - Current workspace ID for evaluation context
 * @param dispatch - Redux dispatch function
 */
export async function evaluateFeatureFlags(
  accessToken: string,
  workspaceId: string,
  dispatch: AppDispatch
): Promise<void> {
  dispatch(setFeatureFlagsLoading());

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FEATURE_EVALUATION_TIMEOUT_MS);

  try {
    const requestBody: FeatureEvaluationRequest = {
      keys: CRM_FEATURE_FLAGS,
      context: { workspaceId },
    };

    const response = await fetch(`${CENTRAL_URL}/api/features/evaluate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Feature evaluation failed: ${response.status}`);
    }

    const data: FeatureEvaluationResponse = await response.json();

    if (!data.ok) {
      throw new Error('Feature evaluation returned ok: false');
    }

    // Transform Central response to simple boolean map for Redux
    const flagMap: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(data.flags)) {
      flagMap[key] = value.enabled;
    }

    dispatch(setFeatureFlags(flagMap));
  } catch (error) {
    // INVARIANT: Fail closed — all flags default to false.
    dispatch(setFeatureFlagsError());

    // Log error but don't throw — feature evaluation failure is non-fatal
    if (error instanceof DOMException && error.name === 'AbortError') {
      console.error('[evaluateFeatureFlags] Request timed out, defaulting all flags to false');
    } else {
      console.error('[evaluateFeatureFlags] Failed, defaulting all flags to false:', error);
    }
  } finally {
    clearTimeout(timeoutId);
  }
}
